import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ClipboardList, Pencil } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import type { Trace, Tool } from '@/features/traceability/type/Tool';
import { errorResult, runJob } from '@/lib/http/runJob';
import type { ToolResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { parseToolResponse } from '../utils/toolResponse';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { useTraceEdit } from '../hooks/useTraceEdit';
import { TraceViewBody } from './TraceViewBody';
import { TraceEditBody } from './TraceEditBody';
import type { TraceEditBodyHandle } from './TraceEditBody';

export type TracesViewAdvancedProps = {
  traceabilityApiClient: TraceabilityApiClient | null;
  toolId: string;
  viewMode?: 'overview' | 'advanced';
  onViewModeChange?: (mode: 'overview' | 'advanced') => void;
  onToolLoad?: (tool: Tool | null) => void;
};

type EditMode = 'view' | 'edit';

export function TracesViewAdvanced({
  traceabilityApiClient,
  toolId,
  viewMode = 'advanced',
  onViewModeChange,
  onToolLoad,
}: TracesViewAdvancedProps) {
  const [tool, setTool] = useState<Tool | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [mode, setMode] = useState<EditMode>('view');
  const [showEmpty, setShowEmpty] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const { t } = useTranslation();

  const editBodyRef = useRef<TraceEditBodyHandle>(null);

  const traces: Trace[] = tool?.traces ?? [];
  const currentTrace = traces[currentIdx] ?? null;

  const { editValues, isSaving, saveError, setField, reset, save } = useTraceEdit(
    traceabilityApiClient,
    currentTrace
  );

  // Reset edit state when the displayed trace changes — index navigation, tool
  // switch, AND the async load/refetch replacing the trace object (otherwise the
  // first-shown trace would open its edit form unseeded).
  useEffect(() => {
    reset();
    setMode('view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, currentIdx, currentTrace]);

  // ── Data fetch ───────────────────────────────────────────────────────────────
  const fetchToolData = useCallback(
    async (signal: AbortSignal) => {
      if (!traceabilityApiClient || !toolId) {
        return errorResult('TraceabilityApiClient or toolId not provided', 'InitializationError');
      }

      return runJob<ToolResponse>({
        initFn: () => traceabilityApiClient.toolRead({ toolId }, { signal }),
        statusFn: (jobId) => traceabilityApiClient.toolStatus({ jobId }, { signal }),
        signal,
        pollingDelayMs: 1000,
        fallbackMessage: 'Failed to load tool data',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [traceabilityApiClient, toolId, refreshKey]
  );

  const { isLoading } = useApiCall<ToolResponse>({
    apiFn: fetchToolData,
    deps: [toolId, refreshKey],
    immediate: !!traceabilityApiClient && !!toolId,
    onSuccess: (data) => {
      const status = (data as { status?: string }).status;
      if (status === 'pending' || status === 'processing') return;

      const nextTool = parseToolResponse(data);
      if (!nextTool) {
        setError(t('toolView.advanced.errors.noDetails'));
        onToolLoad?.(null);
        return;
      }
      setTool(nextTool);
      onToolLoad?.(nextTool);
      // Preserve the position across refetches (e.g. after saving a trace);
      // only clamp when the trace list shrank.
      setCurrentIdx((idx) => Math.min(idx, Math.max(0, (nextTool.traces?.length ?? 1) - 1)));
      setError(null);
    },
    onError: () => {
      setTool(null);
      onToolLoad?.(null);
      setError(t('toolView.traces.errors.unknown'));
    },
  });

  useEffect(() => {
    setTool(null);
    setError(null);
    setCurrentIdx(0);
  }, [toolId]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handlePrev = () => {
    reset();
    setMode('view');
    setCurrentIdx((i) => Math.max(0, i - 1));
  };

  const handleNext = () => {
    reset();
    setMode('view');
    setCurrentIdx((i) => Math.min(traces.length - 1, i + 1));
  };

  const handleModeChange = (next: EditMode) => {
    if (next === 'view') reset();
    setMode(next);
  };

  const handleSave = async () => {
    if (!editBodyRef.current?.validate()) return;
    const ok = await save();
    if (ok) {
      setMode('view');
      setRefreshKey((k) => k + 1);
    }
  };

  const handleDiscard = () => {
    reset();
    setMode('view');
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <section className="flex flex-col gap-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="section-title">{t('toolView.advanced.title')}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full bg-card px-3 py-1 text-sm text-muted-foreground shadow-sm border border-border">
            <Checkbox
              id="advanced-show-empty"
              checked={showEmpty}
              onCheckedChange={(checked) => setShowEmpty(checked === true)}
            />
            <label htmlFor="advanced-show-empty" className="select-none cursor-pointer">
              {t('toolView.advanced.showEmpty')}
            </label>
          </div>
          {onViewModeChange && (
            <Button
              onClick={() => onViewModeChange(viewMode === 'advanced' ? 'overview' : 'advanced')}
            >
              {viewMode === 'advanced'
                ? t('toolView.detail.actions.showOverview')
                : t('toolView.detail.actions.showList')}
            </Button>
          )}
        </div>
      </header>

      {/* Loading */}
      {isLoading && (
        <Callout tone="neutral" loading>
          {t('toolView.advanced.loading')}
        </Callout>
      )}

      {/* Error */}
      {error && <Callout tone="error">{error}</Callout>}

      {/* Save error */}
      {saveError && <Callout tone="error">{saveError}</Callout>}

      {/* No traces */}
      {tool && traces.length === 0 && (
        <EmptyState icon={ClipboardList} size="compact" title={t('toolView.advanced.noTraces')} />
      )}

      {/* Main card */}
      {tool && traces.length > 0 && currentTrace && (
        <>
          {/* Edit mode banner */}
          {mode === 'edit' && (
            <Callout tone="warning" icon={Pencil}>
              {t('toolView.advanced.editMode.banner')}
            </Callout>
          )}

          <Card padding="none" className="overflow-hidden">
            {/* Navigator bar */}
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 bg-card">
              {/* Prev/Next + counter */}
              <div className="flex items-center gap-2">
                <IconButton
                  icon={ChevronLeft}
                  size="icon"
                  label={t('toolView.advanced.navigator.prev')}
                  onClick={handlePrev}
                  disabled={currentIdx === 0}
                />
                <span className="font-mono text-sm text-muted-foreground">
                  {t('toolView.advanced.navigator.counter', {
                    current: String(currentIdx + 1),
                    total: String(traces.length),
                  })}
                </span>
                <IconButton
                  icon={ChevronRight}
                  size="icon"
                  label={t('toolView.advanced.navigator.next')}
                  onClick={handleNext}
                  disabled={currentIdx >= traces.length - 1}
                />
              </div>

              {/* Trace summary */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0 flex-1 justify-center">
                {currentTrace.traceId !== undefined && (
                  <span className="font-mono font-semibold text-foreground">
                    #T-{currentTrace.traceId}
                  </span>
                )}
                {currentTrace.operator && <span className="truncate">{currentTrace.operator}</span>}
              </div>

              {/* VIEW / EDIT toggle */}
              <SegmentedControl<EditMode>
                options={[
                  { value: 'view', label: t('toolView.advanced.navigator.viewMode') },
                  {
                    value: 'edit',
                    label: t('toolView.advanced.navigator.editMode'),
                    tone: 'warning',
                  },
                ]}
                value={mode}
                onChange={handleModeChange}
                aria-label={t('toolView.advanced.navigator.modeLabel')}
              />
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto max-h-[65vh]">
              {mode === 'view' ? (
                <TraceViewBody trace={currentTrace} showEmpty={showEmpty} />
              ) : (
                <TraceEditBody
                  ref={editBodyRef}
                  values={editValues}
                  onChange={setField}
                  traceabilityApiClient={traceabilityApiClient}
                  companyName={tool.companyName ?? ''}
                />
              )}
            </div>

            {/* Action bar — edit mode only */}
            {mode === 'edit' && (
              <div className="flex items-center justify-end gap-3 border-t border-border bg-card px-4 py-3">
                <Button variant="outline" onClick={handleDiscard} disabled={isSaving}>
                  {t('toolView.advanced.editMode.discard')}
                </Button>
                <Button onClick={() => void handleSave()} loading={isSaving}>
                  {isSaving
                    ? t('toolView.advanced.editMode.saving')
                    : t('toolView.advanced.editMode.save')}
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </section>
  );
}
